import { useState, useCallback } from "react";
import { 
    getFigmaCleanupReports, 
    getFigmaCleanupReportDetail, 
    triggerFigmaCleanup 
} from "../api/figmaCleanupClient";
import { 
    FigmaCleanupReportDto, 
    FigmaCleanupReportDetailDto, 
    TriggerFigmaCleanupResult 
} from "../api/models/figmaCleanupModels";

export const useFigmaCleanup = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [reports, setReports] = useState<FigmaCleanupReportDto[]>([]);
    const [selectedReport, setSelectedReport] = useState<FigmaCleanupReportDetailDto | null>(null);
    const [uploadResult, setUploadResult] = useState<TriggerFigmaCleanupResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fetchReports = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getFigmaCleanupReports();
            setReports(data);
        } catch (err) {
            setError("Failed to fetch reports");
            console.error("Error fetching Figma cleanup reports:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchReportDetail = useCallback(async (id: number) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getFigmaCleanupReportDetail(id);
            setSelectedReport(data);
        } catch (err) {
            setError("Failed to fetch report details");
            console.error("Error fetching Figma cleanup report detail:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const uploadCsv = useCallback(async (file: File) => {
        setIsUploading(true);
        setError(null);
        setUploadResult(null);
        try {
            const result = await triggerFigmaCleanup(file);
            setUploadResult(result);
            if (result.success) {
                // Refresh reports after successful upload
                await fetchReports();
            }
            return result;
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || "Failed to trigger cleanup";
            setError(errorMessage);
            console.error("Error triggering Figma cleanup:", err);
            return { success: false, message: errorMessage, usersParsed: 0 };
        } finally {
            setIsUploading(false);
        }
    }, [fetchReports]);

    const clearUploadResult = useCallback(() => {
        setUploadResult(null);
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        isLoading,
        isUploading,
        reports,
        selectedReport,
        uploadResult,
        error,
        fetchReports,
        fetchReportDetail,
        uploadCsv,
        clearUploadResult,
        clearError
    };
};
